import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import SecurityTab from '../components/SecurityTab';
import BackupManager from '../components/BackupManager';
import TestimonialsTab from '../components/TestimonialsTab';
import SettingsTab from '../components/SettingsTab';
import ActivityLogTab from '../components/ActivityLogTab';
import RecycleBinTab from '../components/RecycleBinTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pagination component
const Pagination = ({ currentPage, totalItems, itemsPerPage, onPageChange }) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  if (totalPages <= 1) return null;

  const pages = [];
  const maxVisiblePages = 5;
  
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
  
  if (endPage - startPage < maxVisiblePages - 1) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
            currentPage === 1
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
            currentPage === totalPages
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          Next
        </button>
      </div>
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
            <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of{' '}
            <span className="font-medium">{totalItems}</span> results
          </p>
        </div>
        <div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-500 hover:bg-gray-50'
              }`}
            >
              <span className="sr-only">Previous</span>
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>
            
            {startPage > 1 && (
              <>
                <button
                  onClick={() => onPageChange(1)}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  1
                </button>
                {startPage > 2 && (
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    ...
                  </span>
                )}
              </>
            )}

            {pages.map((page) => (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                  page === currentPage
                    ? 'z-10 bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}

            {endPage < totalPages && (
              <>
                {endPage < totalPages - 1 && (
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    ...
                  </span>
                )}
                <button
                  onClick={() => onPageChange(totalPages)}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {totalPages}
                </button>
              </>
            )}

            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-500 hover:bg-gray-50'
              }`}
            >
              <span className="sr-only">Next</span>
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

// Admin Login Component
export const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null); // Track selected profile
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Require profile selection
    if (!selectedProfile) {
      setError('Please select a login profile (Super Admin or Admin)');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      const token = response.data.access_token;
      localStorage.setItem('admin_token', token);
      
      // Verify user's actual role matches selected profile
      const userInfoResponse = await axios.get(`${API}/admin/account-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const userRole = userInfoResponse.data.role;
      
      // Check if role matches selected profile
      if (selectedProfile === 'super_admin' && userRole !== 'Super Administrator') {
        localStorage.removeItem('admin_token');
        setError('You do not have Super Admin privileges. Please select the Admin profile.');
        setLoading(false);
        return;
      }
      
      if (selectedProfile === 'admin' && userRole === 'Super Administrator') {
        localStorage.removeItem('admin_token');
        setError('You have Super Admin privileges. Please select the Super Admin profile.');
        setLoading(false);
        return;
      }
      
      navigate('/admin/dashboard');
    } catch (err) {
      setError('Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const [resetToken, setResetToken] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${API}/admin/forgot-password`, { email: resetEmail });
      
      if (response.data.reset_token) {
        // Email not configured, show token directly
        setResetToken(response.data.reset_token);
        setShowResetForm(true);
        setSuccess('Reset token generated! Use the form below to set your new password.');
      } else {
        // Email configured, instructions sent
        setSuccess('Password reset instructions have been sent to your email.');
        setTimeout(() => {
          setShowForgotPassword(false);
          setSuccess('');
        }, 3000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate reset token. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }

    setResetLoading(true);
    setError('');

    try {
      await axios.post(`${API}/admin/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      
      setSuccess('Password reset successfully! Redirecting to login...');
      setTimeout(() => {
        setShowForgotPassword(false);
        setShowResetForm(false);
        setResetToken('');
        setResetEmail('');
        setNewPassword('');
        setConfirmNewPassword('');
        setSuccess('');
        setError('');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <h1 className="text-3xl font-bold text-center mb-2">Reset Password</h1>
            {!showResetForm ? (
              <p className="text-gray-600 text-center mb-6">
                Enter your email address to generate a password reset token.
              </p>
            ) : (
              <p className="text-gray-600 text-center mb-6">
                Set your new password below.
              </p>
            )}
            
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
                {success}
              </div>
            )}

            {!showResetForm ? (
              <form onSubmit={handleForgotPassword}>
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    placeholder="admin@example.com"
                    className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>
                <button
                  type="submit"
                  disabled={resetLoading}
                  className="w-full bg-blue-600 text-white py-3 rounded-md hover:bg-blue-700 transition font-medium disabled:bg-gray-400 mb-3"
                >
                  {resetLoading ? 'Generating...' : 'Generate Reset Token'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setError('');
                    setSuccess('');
                    setResetEmail('');
                  }}
                  className="w-full bg-gray-200 text-gray-700 py-3 rounded-md hover:bg-gray-300 transition font-medium"
                >
                  Back to Login
                </button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword}>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    placeholder="Minimum 6 characters"
                    className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-1">Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    placeholder="Re-enter new password"
                    className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>
                <button
                  type="submit"
                  disabled={resetLoading}
                  className="w-full bg-blue-600 text-white py-3 rounded-md hover:bg-blue-700 transition font-medium disabled:bg-gray-400 mb-3"
                >
                  {resetLoading ? 'Resetting...' : 'Reset Password'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setShowResetForm(false);
                    setResetToken('');
                    setResetEmail('');
                    setNewPassword('');
                    setConfirmNewPassword('');
                    setError('');
                    setSuccess('');
                  }}
                  className="w-full bg-gray-200 text-gray-700 py-3 rounded-md hover:bg-gray-300 transition font-medium"
                >
                  Back to Login
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4" data-testid="admin-login-page">
      <div className="max-w-md w-full">
        <div className="bg-white p-8 rounded-lg shadow-lg">
          <h1 className="text-3xl font-bold text-center mb-8">Admin Login</h1>
          
          {/* Profile Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-3 text-center">Select Login Profile <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setSelectedProfile('super_admin')}
                className={`flex flex-col items-center p-4 border-2 rounded-lg transition ${
                  selectedProfile === 'super_admin'
                    ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-300'
                    : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                }`}
              >
                <div className="text-3xl mb-2">👑</div>
                <div className={`font-semibold ${selectedProfile === 'super_admin' ? 'text-blue-900' : 'text-gray-900'}`}>
                  Super Admin
                </div>
                <div className="text-xs text-gray-600 mt-1">Full Access</div>
                {selectedProfile === 'super_admin' && (
                  <div className="mt-2 text-xs text-blue-700 font-medium">✓ Selected</div>
                )}
              </button>
              <button
                type="button"
                onClick={() => setSelectedProfile('admin')}
                className={`flex flex-col items-center p-4 border-2 rounded-lg transition ${
                  selectedProfile === 'admin'
                    ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-300'
                    : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                }`}
              >
                <div className="text-3xl mb-2">👤</div>
                <div className={`font-semibold ${selectedProfile === 'admin' ? 'text-blue-900' : 'text-gray-900'}`}>
                  Admin
                </div>
                <div className="text-xs text-gray-600 mt-1">Standard Access</div>
                {selectedProfile === 'admin' && (
                  <div className="mt-2 text-xs text-blue-700 font-medium">✓ Selected</div>
                )}
              </button>
            </div>
            <div className="mt-3 text-xs text-gray-500 text-center">
              💡 You must select your profile type to login
            </div>
          </div>
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="admin-login-form">
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                data-testid="admin-email"
              />
            </div>
            <div className="mb-2">
              <label className="block text-sm font-medium mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent pr-10"
                  data-testid="admin-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div className="mb-6 text-right">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                Forgot Password?
              </button>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 rounded-md hover:bg-blue-700 transition font-medium disabled:bg-gray-400"
              data-testid="admin-login-btn"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Admin Dashboard Component
export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState(null); // Track user role
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [calculatedQuotes, setCalculatedQuotes] = useState([]);
  const [internalTestQuotes, setInternalTestQuotes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [completedBookings, setCompletedBookings] = useState([]);
  const [visitorStats, setVisitorStats] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [portalAccessRequests, setPortalAccessRequests] = useState([]);
  const [portalUsers, setPortalUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filteredVisitors, setFilteredVisitors] = useState([]);

  // Helper function to format date with time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  // Filter states for each tab - separated into input (typing) and applied (actual filtering)
  const [filterInputs, setFilterInputs] = useState({
    quotes: { searchText: '', startDate: '', endDate: '' },
    contacts: { searchText: '', startDate: '', endDate: '' },
    applications: { searchText: '', startDate: '', endDate: '' },
    calculatedQuotes: { searchText: '', startDate: '', endDate: '', location: '' },
    bookings: { searchText: '', startDate: '', endDate: '', location: '' },
    completedBookings: { searchText: '', startDate: '', endDate: '', location: '' },
    visitors: { searchText: '', startDate: '', endDate: '', ipAddress: '' },
    portalUsers: { searchText: '', startDate: '', endDate: '' },
    accessRequests: { searchText: '', startDate: '', endDate: '' }
  });
  
  const [filters, setFilters] = useState({
    quotes: { searchText: '', startDate: '', endDate: '' },
    contacts: { searchText: '', startDate: '', endDate: '' },
    applications: { searchText: '', startDate: '', endDate: '' },
    calculatedQuotes: { searchText: '', startDate: '', endDate: '', location: '' },
    bookings: { searchText: '', startDate: '', endDate: '', location: '' },
    completedBookings: { searchText: '', startDate: '', endDate: '', location: '' },
    visitors: { searchText: '', startDate: '', endDate: '', ipAddress: '' },
    portalUsers: { searchText: '', startDate: '', endDate: '' },
    accessRequests: { searchText: '', startDate: '', endDate: '' }
  });
  
  // Bulk selection states
  const [selectedItems, setSelectedItems] = useState({
    quotes: [],
    contacts: [],
    applications: [],
    calculatedQuotes: [],
    bookings: [],
    completedBookings: [],
    visitors: [],
    portalUsers: [],
    accessRequests: [],
    internalTestQuotes: []
  });

  // Pagination state for each data type
  const [quotesPage, setQuotesPage] = useState(1);
  const [quotesTotal, setQuotesTotal] = useState(0);
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [applicationsPage, setApplicationsPage] = useState(1);
  const [applicationsTotal, setApplicationsTotal] = useState(0);
  const [calculatedQuotesPage, setCalculatedQuotesPage] = useState(1);
  const [calculatedQuotesTotal, setCalculatedQuotesTotal] = useState(0);
  const [internalTestQuotesPage, setInternalTestQuotesPage] = useState(1);
  const [internalTestQuotesTotal, setInternalTestQuotesTotal] = useState(0);
  const [bookingsPage, setBookingsPage] = useState(1);
  const [bookingsTotal, setBookingsTotal] = useState(0);
  const [completedBookingsPage, setCompletedBookingsPage] = useState(1);
  const [completedBookingsTotal, setCompletedBookingsTotal] = useState(0);
  const [visitorsPage, setVisitorsPage] = useState(1);
  const [visitorsTotal, setVisitorsTotal] = useState(0);
  
  const itemsPerPage = 50;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };
  
  // Apply filters when search button is clicked
  const applyFilters = useCallback((tabKey) => {
    setFilters(prev => ({
      ...prev,
      [tabKey]: filterInputs[tabKey]
    }));
  }, [filterInputs]);
  
  // Clear filters
  const clearFilters = useCallback((tabKey) => {
    setFilterInputs(prev => ({
      ...prev,
      [tabKey]: { searchText: '', startDate: '', endDate: '', location: '', ipAddress: '' }
    }));
    setFilters(prev => ({
      ...prev,
      [tabKey]: { searchText: '', startDate: '', endDate: '', location: '', ipAddress: '' }
    }));
  }, []);
  
  // Toggle selection for an item
  const toggleSelection = (tabKey, itemId) => {
    setSelectedItems(prev => ({
      ...prev,
      [tabKey]: prev[tabKey].includes(itemId)
        ? prev[tabKey].filter(id => id !== itemId)
        : [...prev[tabKey], itemId]
    }));
  };
  
  // Select all items in current view
  const selectAll = (tabKey, items) => {
    const itemIds = items.map(item => item.id || item.email || item._id);
    setSelectedItems(prev => ({
      ...prev,
      [tabKey]: itemIds
    }));
  };
  
  // Clear selection
  const clearSelection = (tabKey) => {
    setSelectedItems(prev => ({
      ...prev,
      [tabKey]: []
    }));
  };

  // Generic filter function
  const filterData = (data, filterKey) => {
    const filter = filters[filterKey];
    if (!filter) return data;

    return data.filter(item => {
      // Text search (email, name, IP, location)
      if (filter.searchText) {
        const searchLower = filter.searchText.toLowerCase();
        const matchText = 
          (item.email && item.email.toLowerCase().includes(searchLower)) ||
          (item.name && item.name.toLowerCase().includes(searchLower)) ||
          (item.customer_name && item.customer_name.toLowerCase().includes(searchLower)) ||
          (item.customer_email && item.customer_email.toLowerCase().includes(searchLower)) ||
          (item.ip_address && item.ip_address.includes(searchLower)) ||
          (item.pickup_location && item.pickup_location.toLowerCase().includes(searchLower)) ||
          (item.delivery_location && item.delivery_location.toLowerCase().includes(searchLower)) ||
          (item.location && item.location.toLowerCase().includes(searchLower));
        
        if (!matchText) return false;
      }

      // Date range filter
      if (filter.startDate || filter.endDate) {
        const itemDate = new Date(item.created_at || item.requested_at || item.timestamp);
        if (filter.startDate) {
          const start = new Date(filter.startDate);
          if (itemDate < start) return false;
        }
        if (filter.endDate) {
          const end = new Date(filter.endDate);
          end.setHours(23, 59, 59);
          if (itemDate > end) return false;
        }
      }

      // IP Address filter
      if (filter.ipAddress && item.ip_address) {
        if (!item.ip_address.includes(filter.ipAddress)) return false;
      }

      // Location filter
      if (filter.location) {
        const locationLower = filter.location.toLowerCase();
        const matchLocation = 
          (item.pickup_location && item.pickup_location.toLowerCase().includes(locationLower)) ||
          (item.delivery_location && item.delivery_location.toLowerCase().includes(locationLower)) ||
          (item.location && item.location.toLowerCase().includes(locationLower));
        
        if (!matchLocation) return false;
      }

      return true;
    });
  };

  const updateFilter = useCallback((filterKey, field, value) => {
    setFilterInputs(prev => ({
      ...prev,
      [filterKey]: {
        ...prev[filterKey],
        [field]: value
      }
    }));
  }, []);

  // Individual fetch functions for each data type with pagination
  const fetchQuotes = async (page) => {
    try {
      const response = await axios.get(
        `${API}/quotes?page=${page}&limit=${itemsPerPage}`,
        getAuthHeaders()
      );
      const apiData = response.data;
      const items = apiData.data || apiData.items || apiData;
      const total = apiData.pagination?.total || apiData.total || items.length;
      setQuotes(items);
      setQuotesTotal(total);
    } catch (err) {
      console.error('Failed to fetch quotes:', err);
      if (err.response?.status === 404) {
        setQuotes([]);
        setQuotesTotal(0);
      }
    }
  };

  const fetchContacts = async (page) => {
    try {
      const response = await axios.get(
        `${API}/admin/contacts?page=${page}&limit=${itemsPerPage}`,
        getAuthHeaders()
      );
      const apiData = response.data;
      const items = apiData.data || apiData.items || apiData;
      const total = apiData.pagination?.total || apiData.total || items.length;
      setContacts(items);
      setContactsTotal(total);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    }
  };

  const fetchApplications = async (page) => {
    try {
      const response = await axios.get(
        `${API}/admin/applications?page=${page}&limit=${itemsPerPage}`,
        getAuthHeaders()
      );
      const apiData = response.data;
      const items = apiData.data || apiData.items || apiData;
      const total = apiData.pagination?.total || apiData.total || items.length;
      setApplications(items);
      setApplicationsTotal(total);
    } catch (err) {
      console.error('Failed to fetch applications:', err);
    }
  };

  const fetchCalculatedQuotes = async (page) => {
    try {
      const response = await axios.get(
        `${API}/admin/calculated-quotes?page=${page}&limit=${itemsPerPage}`,
        getAuthHeaders()
      );
      const apiData = response.data;
      const items = apiData.data || apiData.items || apiData;
      const total = apiData.pagination?.total || apiData.total || items.length;
      setCalculatedQuotes(items);
      setCalculatedQuotesTotal(total);
    } catch (err) {
      console.error('Failed to fetch calculated quotes:', err);
    }
  };

  const fetchInternalTestQuotes = async (page) => {
    try {
      const response = await axios.get(
        `${API}/admin/internal-quotes?page=${page}&limit=${itemsPerPage}`,
        getAuthHeaders()
      );
      const apiData = response.data;
      const items = apiData.data || apiData.items || apiData;
      const total = apiData.pagination?.total || apiData.total || items.length;
      setInternalTestQuotes(items);
      setInternalTestQuotesTotal(total);
    } catch (err) {
      console.error('Failed to fetch internal test quotes:', err);
      if (err.response?.status === 404) {
        setInternalTestQuotes([]);
        setInternalTestQuotesTotal(0);
      }
    }
  };

  const fetchBookings = async (page) => {
    try {
      const response = await axios.get(
        `${API}/admin/bookings?page=${page}&limit=${itemsPerPage}`,
        getAuthHeaders()
      );
      const apiData = response.data;
      const items = apiData.data || apiData.items || apiData;
      const total = apiData.pagination?.total || apiData.total || items.length;
      setBookings(items);
      setBookingsTotal(total);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
      if (err.response?.status === 404) {
        setBookings([]);
        setBookingsTotal(0);
      }
    }
  };

  const fetchCompletedBookings = async (page) => {
    try {
      const response = await axios.get(
        `${API}/admin/bookings/completed?page=${page}&limit=${itemsPerPage}`,
        getAuthHeaders()
      );
      const apiData = response.data;
      const items = apiData.data || apiData.items || apiData;
      const total = apiData.pagination?.total || apiData.total || items.length;
      setCompletedBookings(items);
      setCompletedBookingsTotal(total);
    } catch (err) {
      console.error('Failed to fetch completed bookings:', err);
      if (err.response?.status === 404) {
        setCompletedBookings([]);
        setCompletedBookingsTotal(0);
      }
    }
  };

  const fetchVisitors = async (page) => {
    try {
      const response = await axios.get(
        `${API}/visitors?page=${page}&limit=${itemsPerPage}`,
        getAuthHeaders()
      );
      const apiData = response.data;
      const items = apiData.data || apiData.items || apiData;
      const total = apiData.pagination?.total || apiData.total || items.length;
      setVisitors(items);
      setVisitorsTotal(total);
    } catch (err) {
      console.error('Failed to fetch visitors:', err);
    }
  };

  const fetchPortalAccessRequests = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/portal-access-requests`,
        getAuthHeaders()
      );
      setPortalAccessRequests(response.data.requests || []);
    } catch (err) {
      console.error('Failed to fetch portal access requests:', err);
      setPortalAccessRequests([]);
    }
  };

  const fetchPortalUsers = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/portal-users`,
        getAuthHeaders()
      );
      setPortalUsers(response.data.users || []);
    } catch (err) {
      console.error('Failed to fetch portal users:', err);
      setPortalUsers([]);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, visitorRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, getAuthHeaders()),
        axios.get(`${API}/visitor-stats`, getAuthHeaders())
      ]);

      setStats(statsRes.data);
      setVisitorStats(visitorRes.data);
      
      // Fetch paginated data
      await Promise.all([
        fetchQuotes(quotesPage),
        fetchContacts(contactsPage),
        fetchApplications(applicationsPage),
        fetchCalculatedQuotes(calculatedQuotesPage),
        fetchInternalTestQuotes(internalTestQuotesPage),
        fetchBookings(bookingsPage),
        fetchCompletedBookings(completedBookingsPage),
        fetchVisitors(visitorsPage),
        fetchPortalAccessRequests(),
        fetchPortalUsers()
      ]);
      
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setLoading(false);
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchUserRole();
    fetchData();
  }, [navigate]);
  
  const fetchUserRole = async () => {
    try {
      const response = await axios.get(`${API}/admin/account-info`, getAuthHeaders());
      setUserRole(response.data.role);
    } catch (error) {
      console.error('Failed to fetch user role:', error);
      // Default to admin if fetch fails
      setUserRole('Administrator');
    }
  };

  useEffect(() => {
    setFilteredVisitors(visitors);
  }, [visitors]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const fetchFilteredVisitors = async (page = 1) => {
    try {
      let url = `${API}/visitors?page=${page}&limit=${itemsPerPage}`;
      if (startDate) {
        url += `&start_date=${startDate}T00:00:00`;
      }
      if (endDate) {
        url += `&end_date=${endDate}T23:59:59`;
      }
      const response = await axios.get(url, getAuthHeaders());
      const apiData = response.data;
      const items = apiData.data || apiData.items || apiData;
      const total = apiData.pagination?.total || apiData.total || items.length;
      setFilteredVisitors(items);
      setVisitorsTotal(total);
      setVisitorsPage(page);
    } catch (err) {
      console.error('Failed to fetch filtered visitors');
    }
  };

  const handleDateFilter = () => {
    setVisitorsPage(1);
    fetchFilteredVisitors(1);
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    setVisitorsPage(1);
    fetchVisitors(1);
  };

  const updateQuoteStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/quotes/${id}`, { status }, getAuthHeaders());
      fetchData();
    } catch (err) {
      console.error('Failed to update quote status');
    }
  };

  const updateContactStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/contacts/${id}`, { status }, getAuthHeaders());
      fetchData();
    } catch (err) {
      console.error('Failed to update contact status');
    }
  };

  const updateApplicationStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/applications/${id}`, { status }, getAuthHeaders());
      fetchData();
    } catch (err) {
      console.error('Failed to update application status');
    }
  };

  const openDetailsModal = (item, type) => {
    setSelectedItem(item);
    setModalType(type);
  };

  const closeModal = () => {
    setSelectedItem(null);
    setModalType(null);
  };

  const deleteQuote = async (id) => {
    if (window.confirm('Are you sure you want to delete this quote?')) {
      try {
        await axios.delete(`${API}/quotes/${id}`, getAuthHeaders());
        fetchData();
      } catch (err) {
        console.error('Failed to delete quote');
      }
    }
  };

  const deleteContact = async (id) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await axios.delete(`${API}/contacts/${id}`, getAuthHeaders());
        fetchData();
      } catch (err) {
        console.error('Failed to delete contact');
      }
    }
  };

  const deleteApplication = async (id) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      try {
        await axios.delete(`${API}/applications/${id}`, getAuthHeaders());
        fetchData();
      } catch (err) {
        console.error('Failed to delete application');
      }
    }
  };

  const updateCalculatedQuoteStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/calculated-quotes/${id}`, { status }, getAuthHeaders());
      fetchData();
    } catch (err) {
      console.error('Failed to update calculated quote status');
    }
  };

  const deleteCalculatedQuote = async (id) => {
    if (window.confirm('Are you sure you want to delete this calculated quote?')) {
      try {
        await axios.delete(`${API}/calculated-quotes/${id}`, getAuthHeaders());
        fetchData();
      } catch (err) {
        console.error('Failed to delete calculated quote');
      }
    }
  };

  const updateInternalTestQuoteStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/internal-quotes/${id}`, { status }, getAuthHeaders());
      fetchInternalTestQuotes(internalTestQuotesPage);
    } catch (err) {
      console.error('Failed to update internal test quote status');
    }
  };

  const deleteInternalTestQuote = async (id) => {
    if (window.confirm('Are you sure you want to delete this internal test quote?')) {
      try {
        await axios.delete(`${API}/admin/internal-quotes/${id}`, getAuthHeaders());
        fetchInternalTestQuotes(internalTestQuotesPage);
      } catch (err) {
        console.error('Failed to delete internal test quote');
      }
    }
  };

  const updateBookingStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${id}`, { status }, getAuthHeaders());
      fetchBookings(bookingsPage);
    } catch (err) {
      console.error('Failed to update booking status');
    }
  };

  const deleteBooking = async (id) => {
    if (window.confirm('Are you sure you want to delete this booking?')) {
      try {
        await axios.delete(`${API}/admin/bookings/${id}`, getAuthHeaders());
        fetchBookings(bookingsPage);
      } catch (err) {
        console.error('Failed to delete booking');
      }
    }
  };

  const deleteVisitor = async (id) => {
    if (window.confirm('Are you sure you want to delete this visitor record?')) {
      try {
        await axios.delete(`${API}/admin/visitors/${id}`, getAuthHeaders());
        fetchVisitors(visitorsPage);
      } catch (err) {
        console.error('Failed to delete visitor record');
      }
    }
  };

  // Pagination handlers
  const handleSuspendUser = async (userId) => {
    if (!window.confirm('Suspend this user account? They will not be able to login.')) return;
    
    try {
      await axios.put(
        `${API}/admin/portal-users/${userId}/status`,
        { status: 'suspended' },
        getAuthHeaders()
      );
      alert('User account suspended. They will receive an email notification.');
      fetchPortalUsers();
    } catch (err) {
      console.error('Failed to suspend user:', err);
      alert('Failed to suspend user. Please try again.');
    }
  };

  const handleActivateUser = async (userId) => {
    if (!window.confirm('Activate this user account?')) return;
    
    try {
      await axios.put(
        `${API}/admin/portal-users/${userId}/status`,
        { status: 'active' },
        getAuthHeaders()
      );
      alert('User account activated. They can now login.');
      fetchPortalUsers();
    } catch (err) {
      console.error('Failed to activate user:', err);
      alert('Failed to activate user. Please try again.');
    }
  };

  const handleRevokeUser = async (userId) => {
    if (!window.confirm('⚠️ REVOKE this user account permanently? This action is final.')) return;
    
    try {
      await axios.put(
        `${API}/admin/portal-users/${userId}/status`,
        { status: 'revoked' },
        getAuthHeaders()
      );
      alert('User account revoked. They will receive an email notification.');
      fetchPortalUsers();
    } catch (err) {
      console.error('Failed to revoke user:', err);
      alert('Failed to revoke user. Please try again.');
    }
  };

  const handleDeletePortalUser = async (userId) => {
    if (!window.confirm(`⚠️ DELETE this user permanently? This action cannot be undone!`)) return;
    
    try {
      await axios.delete(
        `${API}/admin/portal-users/${userId}`,
        getAuthHeaders()
      );
      alert('User deleted successfully.');
      fetchPortalUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert(err.response?.data?.detail || 'Failed to delete user. Please try again.');
    }
  };

  const handleBulkDeletePortalUsers = async () => {
    const count = selectedItems.portalUsers.length;
    if (!window.confirm(`⚠️ DELETE ${count} selected users permanently? This action cannot be undone!`)) return;
    
    try {
      await Promise.all(
        selectedItems.portalUsers.map(userId =>
          axios.delete(`${API}/admin/portal-users/${userId}`, getAuthHeaders())
        )
      );
      alert(`${count} users deleted successfully.`);
      clearSelection('portalUsers');
      fetchPortalUsers();
    } catch (err) {
      console.error('Failed to delete users:', err);
      alert('Failed to delete some users. Please try again.');
    }
  };

  const handleResetUserPassword = async (userEmail) => {
    const newPassword = prompt(`Enter new temporary password for ${userEmail}:\n\n(User will receive this via email)`);
    if (!newPassword) return;
    
    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/portal-users/${userEmail}/reset-password`,
        { new_password: newPassword },
        getAuthHeaders()
      );
      alert('Password reset successfully! User will receive an email with the new password.');
    } catch (err) {
      console.error('Failed to reset password:', err);
      alert('Failed to reset password. Please try again.');
    }
  };

  const handleApproveAccessRequest = async (requestId) => {
    if (!window.confirm('Approve this portal access request?')) return;
    
    try {
      await axios.put(
        `${API}/admin/portal-access-requests/${requestId}`,
        { status: 'approved', admin_notes: '' },
        getAuthHeaders()
      );
      alert('Access request approved! User will receive an email notification.');
      fetchPortalAccessRequests();
      fetchPortalUsers(); // Refresh portal users list
    } catch (err) {
      console.error('Failed to approve request:', err);
      alert('Failed to approve request. Please try again.');
    }
  };

  const handleRejectAccessRequest = async (requestId) => {
    const notes = prompt('Enter rejection reason (optional):');
    if (notes === null) return; // User cancelled
    
    try {
      await axios.put(
        `${API}/admin/portal-access-requests/${requestId}`,
        { status: 'rejected', admin_notes: notes || 'Request rejected by admin' },
        getAuthHeaders()
      );
      alert('Access request rejected.');
      fetchPortalAccessRequests();
      fetchPortalUsers(); // Refresh portal users list
    } catch (err) {
      console.error('Failed to reject request:', err);
      alert('Failed to reject request. Please try again.');
    }
  };

  const handleRevertAccessRequest = async (requestId) => {
    if (!window.confirm('Revert this request back to Pending status?')) return;
    
    try {
      await axios.put(
        `${API}/admin/portal-access-requests/${requestId}`,
        { status: 'pending', admin_notes: 'Reverted to pending by admin' },
        getAuthHeaders()
      );
      alert('Access request reverted to pending.');
      fetchPortalAccessRequests();
    } catch (err) {
      console.error('Failed to revert request:', err);
      alert('Failed to revert request. Please try again.');
    }
  };

  const handleDeleteAccessRequest = async (requestId) => {
    if (!window.confirm('⚠️ DELETE this access request permanently?')) return;
    
    try {
      await axios.delete(
        `${API}/admin/portal-access-requests/${requestId}`,
        getAuthHeaders()
      );
      alert('Access request deleted.');
      fetchPortalAccessRequests();
    } catch (err) {
      console.error('Failed to delete request:', err);
      alert(err.response?.data?.detail || 'Failed to delete request. Please try again.');
    }
  };

  const handleBulkDeleteAccessRequests = async () => {
    const count = selectedItems.accessRequests.length;
    if (!window.confirm(`⚠️ DELETE ${count} selected access requests permanently?`)) return;
    
    try {
      await Promise.all(
        selectedItems.accessRequests.map(requestId =>
          axios.delete(`${API}/admin/portal-access-requests/${requestId}`, getAuthHeaders())
        )
      );
      alert(`${count} access requests deleted successfully.`);
      clearSelection('accessRequests');
      fetchPortalAccessRequests();
    } catch (err) {
      console.error('Failed to delete requests:', err);
      alert('Failed to delete some requests. Please try again.');
    }
  };

  const handleBulkDeleteBookings = async () => {
    const count = selectedItems.bookings.length;
    if (!window.confirm(`⚠️ DELETE ${count} selected bookings permanently? This action cannot be undone!`)) return;
    
    try {
      await Promise.all(
        selectedItems.bookings.map(bookingId =>
          axios.delete(`${API}/admin/bookings/${bookingId}`, getAuthHeaders())
        )
      );
      alert(`${count} bookings deleted successfully.`);
      clearSelection('bookings');
      fetchBookings();
    } catch (err) {
      console.error('Failed to delete bookings:', err);
      alert('Failed to delete some bookings. Please try again.');
    }
  };

  const handleBulkDeleteCompletedBookings = async () => {
    const count = selectedItems.completedBookings.length;
    if (!window.confirm(`⚠️ DELETE ${count} selected completed bookings permanently?`)) return;
    
    try {
      await Promise.all(
        selectedItems.completedBookings.map(bookingId =>
          axios.delete(`${API}/admin/bookings/${bookingId}`, getAuthHeaders())
        )
      );
      alert(`${count} completed bookings deleted successfully.`);
      clearSelection('completedBookings');
      fetchCompletedBookings();
    } catch (err) {
      console.error('Failed to delete bookings:', err);
      alert('Failed to delete some bookings. Please try again.');
    }
  };

  const handleBulkDeleteQuotes = async () => {
    const count = selectedItems.quotes.length;
    if (!window.confirm(`⚠️ DELETE ${count} selected quotes permanently?`)) return;
    
    try {
      await Promise.all(
        selectedItems.quotes.map(quoteId =>
          axios.delete(`${API}/quotes/${quoteId}`, getAuthHeaders())
        )
      );
      alert(`${count} quotes deleted successfully.`);
      clearSelection('quotes');
      fetchQuotes();
    } catch (err) {
      console.error('Failed to delete quotes:', err);
      alert('Failed to delete some quotes. Please try again.');
    }
  };

  const handleBulkDeleteCalculatedQuotes = async () => {
    const count = selectedItems.calculatedQuotes.length;
    if (!window.confirm(`⚠️ DELETE ${count} selected calculated quotes permanently?`)) return;
    
    try {
      await Promise.all(
        selectedItems.calculatedQuotes.map(quoteId =>
          axios.delete(`${API}/calculated-quotes/${quoteId}`, getAuthHeaders())
        )
      );
      alert(`${count} calculated quotes deleted successfully.`);
      clearSelection('calculatedQuotes');
      fetchCalculatedQuotes();
    } catch (err) {
      console.error('Failed to delete calculated quotes:', err);
      alert('Failed to delete some calculated quotes. Please try again.');
    }
  };

  const handleBulkDeleteContacts = async () => {
    const count = selectedItems.contacts.length;
    if (!window.confirm(`⚠️ DELETE ${count} selected contacts permanently?`)) return;
    
    try {
      await Promise.all(
        selectedItems.contacts.map(contactId =>
          axios.delete(`${API}/contacts/${contactId}`, getAuthHeaders())
        )
      );
      alert(`${count} contacts deleted successfully.`);
      clearSelection('contacts');
      fetchContacts();
    } catch (err) {
      console.error('Failed to delete contacts:', err);
      alert('Failed to delete some contacts. Please try again.');
    }
  };

  const handleBulkDeleteApplications = async () => {
    const count = selectedItems.applications.length;
    if (!window.confirm(`⚠️ DELETE ${count} selected applications permanently?`)) return;
    
    try {
      await Promise.all(
        selectedItems.applications.map(appId =>
          axios.delete(`${API}/applications/${appId}`, getAuthHeaders())
        )
      );
      alert(`${count} applications deleted successfully.`);
      clearSelection('applications');
      fetchApplications();
    } catch (err) {
      console.error('Failed to delete applications:', err);
      alert('Failed to delete some applications. Please try again.');
    }
  };

  const handleBulkDeleteVisitors = async () => {
    const count = selectedItems.visitors.length;
    if (!window.confirm(`⚠️ DELETE ${count} selected visitor records permanently?`)) return;
    
    try {
      await Promise.all(
        selectedItems.visitors.map(visitorId =>
          axios.delete(`${API}/admin/visitors/${visitorId}`, getAuthHeaders())
        )
      );
      alert(`${count} visitor records deleted successfully.`);
      clearSelection('visitors');
      fetchVisitors();
    } catch (err) {
      console.error('Failed to delete visitors:', err);
      alert('Failed to delete some visitor records. Please try again.');
    }
  };

  // Bulk delete for Internal Test Quotes
  const handleBulkDeleteInternalQuotes = async () => {
    const count = selectedItems.internalTestQuotes.length;
    if (count === 0) {
      alert('Please select internal quotes to delete');
      return;
    }
    if (!window.confirm(`Delete ${count} selected internal test quotes? They will be moved to the Recycle Bin.`)) return;
    
    try {
      await Promise.all(
        selectedItems.internalTestQuotes.map(id =>
          axios.delete(`${API}/admin/internal-quotes/${id}`, getAuthHeaders())
        )
      );
      alert(`${count} internal quotes moved to recycle bin`);
      clearSelection('internalTestQuotes');
      fetchInternalTestQuotes(internalTestQuotesPage);
    } catch (err) {
      console.error('Bulk delete failed:', err);
      alert('Some items failed to delete');
    }
  };

  const handleQuotesPageChange = (newPage) => {
    setQuotesPage(newPage);
    fetchQuotes(newPage);
  };

  const handleContactsPageChange = (newPage) => {
    setContactsPage(newPage);
    fetchContacts(newPage);
  };

  const handleApplicationsPageChange = (newPage) => {
    setApplicationsPage(newPage);
    fetchApplications(newPage);
  };

  const handleCalculatedQuotesPageChange = (newPage) => {
    setCalculatedQuotesPage(newPage);
    fetchCalculatedQuotes(newPage);
  };

  const handleInternalTestQuotesPageChange = (newPage) => {
    setInternalTestQuotesPage(newPage);
    fetchInternalTestQuotes(newPage);
  };

  const handleBookingsPageChange = (newPage) => {
    setBookingsPage(newPage);
    fetchBookings(newPage);
  };

  const handleCompletedBookingsPageChange = (newPage) => {
    setCompletedBookingsPage(newPage);
    fetchCompletedBookings(newPage);
  };

  const handleVisitorsPageChange = (newPage) => {
    setVisitorsPage(newPage);
    fetchVisitors(newPage);
  };

  // Filter Bar Component
  const FilterBar = ({ filterKey, showLocation = false, showIP = false }) => {
    const [localSearchText, setLocalSearchText] = useState('');
    const [localLocation, setLocalLocation] = useState('');
    const [localIP, setLocalIP] = useState('');
    
    const filter = filterInputs[filterKey] || {};
    const appliedFilter = filters[filterKey] || {};
    const hasActiveFilters = appliedFilter.searchText || appliedFilter.startDate || appliedFilter.endDate || appliedFilter.location || appliedFilter.ipAddress;
    
    // Sync local state when applied filters change
    useEffect(() => {
      if (appliedFilter.searchText !== localSearchText && appliedFilter.searchText !== undefined) {
        setLocalSearchText(appliedFilter.searchText || '');
      }
    }, [appliedFilter.searchText]);
    
    const handleSearch = () => {
      updateFilter(filterKey, 'searchText', localSearchText);
      if (showLocation) updateFilter(filterKey, 'location', localLocation);
      if (showIP) updateFilter(filterKey, 'ipAddress', localIP);
      setTimeout(() => applyFilters(filterKey), 50);
    };
    
    return (
      <div className="bg-gray-50 p-4 rounded-lg mb-4 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search Input */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Search (Name/Email{showIP ? '/IP' : ''})
            </label>
            <input
              type="text"
              placeholder="Search..."
              value={localSearchText}
              onChange={(e) => setLocalSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearch();
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filter.startDate || ''}
              onChange={(e) => {
                updateFilter(filterKey, 'startDate', e.target.value);
                // Auto-apply when date is selected
                setTimeout(() => applyFilters(filterKey), 100);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filter.endDate || ''}
              onChange={(e) => {
                updateFilter(filterKey, 'endDate', e.target.value);
                // Auto-apply when date is selected
                setTimeout(() => applyFilters(filterKey), 100);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Location Filter */}
          {showLocation && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                placeholder="City, State..."
                value={localLocation}
                onChange={(e) => setLocalLocation(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
              />
            </div>
          )}

          {/* IP Address Filter */}
          {showIP && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                IP Address
              </label>
              <input
                type="text"
                placeholder="192.168..."
                value={localIP}
                onChange={(e) => setLocalIP(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Search and Clear Buttons */}
          <div className="flex items-end gap-2">
            <button
              onClick={handleSearch}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition font-medium"
            >
              🔍 Search
            </button>
            {hasActiveFilters && (
              <button
                onClick={() => {
                  setLocalSearchText('');
                  setLocalLocation('');
                  setLocalIP('');
                  clearFilters(filterKey);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300 transition"
                title="Clear Filters"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Active Filter Indicator */}
        {hasActiveFilters && (
          <div className="mt-3 flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded">
            <div className="text-xs text-blue-800">
              <span className="font-medium">Active filters: </span>
              {appliedFilter.searchText && <span className="bg-blue-100 px-2 py-1 rounded mx-1">{appliedFilter.searchText}</span>}
              {appliedFilter.startDate && <span className="bg-blue-100 px-2 py-1 rounded mx-1">From: {appliedFilter.startDate}</span>}
              {appliedFilter.endDate && <span className="bg-blue-100 px-2 py-1 rounded mx-1">To: {appliedFilter.endDate}</span>}
            </div>
            <button
              onClick={() => clearFilters(filterKey)}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              Clear All
            </button>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-dashboard">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <button
            onClick={handleLogout}
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition"
            data-testid="admin-logout-btn"
          >
            Logout
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex space-x-4 mb-8 border-b">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-2 px-4 font-medium ${
              activeTab === 'overview'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600'
            }`}
            data-testid="tab-overview"
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('allQuotes')}
            className={`pb-2 px-4 font-medium ${
              activeTab === 'allQuotes'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600'
            }`}
            data-testid="tab-all-quotes"
          >
            All Quotes ({quotesTotal + calculatedQuotesTotal})
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`pb-2 px-4 font-medium ${
              activeTab === 'contacts'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600'
            }`}
            data-testid="tab-contacts"
          >
            Contacts ({contactsTotal})
          </button>
          <button
            onClick={() => setActiveTab('applications')}
            className={`pb-2 px-4 font-medium ${
              activeTab === 'applications'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600'
            }`}
            data-testid="tab-applications"
          >
            Applications ({applicationsTotal})
          </button>
          <button
            onClick={() => setActiveTab('internalTestQuotes')}
            className={`pb-2 px-4 font-medium ${
              activeTab === 'internalTestQuotes'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600'
            }`}
            data-testid="tab-internal-test-quotes"
          >
            Internal Test Quotes ({internalTestQuotesTotal})
          </button>
          <button
            onClick={() => setActiveTab('bookings')}
            className={`pb-2 px-4 font-medium ${
              activeTab === 'bookings'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600'
            }`}
            data-testid="tab-bookings"
          >
            Bookings ({bookingsTotal})
          </button>
          <button
            onClick={() => setActiveTab('completedBookings')}
            className={`pb-2 px-4 font-medium ${
              activeTab === 'completedBookings'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600'
            }`}
            data-testid="tab-completed-bookings"
          >
            Completed ({completedBookingsTotal})
          </button>
          {/* Super Admin Only Tabs */}
          {userRole === 'Super Administrator' && (
            <>
              <button
                onClick={() => setActiveTab('visitors')}
                className={`pb-2 px-4 font-medium ${
                  activeTab === 'visitors'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
                data-testid="tab-visitors"
              >
                Visitors ({visitorStats ? visitorStats.total_visits : visitorsTotal})
              </button>
              <button
                onClick={() => setActiveTab('portalAccessRequests')}
                className={`pb-2 px-4 font-medium ${
                  activeTab === 'portalAccessRequests'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
                data-testid="tab-portal-access-requests"
              >
                Portal Access
              </button>
              <button
                onClick={() => setActiveTab('security')}
                className={`pb-2 px-4 font-medium ${
                  activeTab === 'security'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
                data-testid="tab-security"
              >
                🔒 Security
              </button>
              <button
                onClick={() => setActiveTab('backups')}
                className={`pb-2 px-4 font-medium ${
                  activeTab === 'backups'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
                data-testid="tab-backups"
              >
                💾 Backups
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`pb-2 px-4 font-medium ${
                  activeTab === 'settings'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
                data-testid="tab-settings"
              >
                ⚙️ Settings
              </button>
              <button
                onClick={() => setActiveTab('activityLog')}
                className={`pb-2 px-4 font-medium ${
                  activeTab === 'activityLog'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
                data-testid="tab-activity-log"
              >
                📋 Activity Log
              </button>
              <button
                onClick={() => setActiveTab('recycleBin')}
                className={`pb-2 px-4 font-medium ${
                  activeTab === 'recycleBin'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
                data-testid="tab-recycle-bin"
              >
                🗑️ Recycle Bin
              </button>
              <button
                onClick={() => setActiveTab('testimonials')}
                className={`pb-2 px-4 font-medium ${
                  activeTab === 'testimonials'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
                data-testid="tab-testimonials"
              >
                ⭐ Testimonials
              </button>
            </>
          )}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div>
            {/* Analytics Dashboard */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">📊 Analytics & Business Insights</h2>
              <AnalyticsDashboard getAuthHeaders={getAuthHeaders} />
            </div>

            {/* Quick Stats Cards */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Quick Stats</h2>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div 
                onClick={() => setActiveTab('quotes')} 
                className="bg-white p-6 rounded-lg shadow cursor-pointer hover:shadow-lg transition-shadow duration-200" 
                data-testid="stat-card-quotes"
              >
                <h3 className="text-gray-600 mb-2">Quote Requests</h3>
                <p className="text-3xl font-bold">{stats.total_quotes}</p>
                <p className="text-sm text-orange-600 mt-2">{stats.pending_quotes} pending</p>
              </div>
              <div 
                onClick={() => setActiveTab('contacts')} 
                className="bg-white p-6 rounded-lg shadow cursor-pointer hover:shadow-lg transition-shadow duration-200" 
                data-testid="stat-card-contacts"
              >
                <h3 className="text-gray-600 mb-2">Contact Submissions</h3>
                <p className="text-3xl font-bold">{stats.total_contacts}</p>
                <p className="text-sm text-blue-600 mt-2">{stats.new_contacts} new</p>
              </div>
              <div 
                onClick={() => setActiveTab('applications')} 
                className="bg-white p-6 rounded-lg shadow cursor-pointer hover:shadow-lg transition-shadow duration-200" 
                data-testid="stat-card-apps"
              >
                <h3 className="text-gray-600 mb-2">Job Applications</h3>
                <p className="text-3xl font-bold">{stats.total_applications}</p>
                <p className="text-sm text-green-600 mt-2">{stats.pending_applications} pending</p>
              </div>
            </div>

            {/* Visitor Statistics - Super Admin Only */}
            {visitorStats && userRole === 'Super Administrator' && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg shadow mb-8">
                <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Website Visitor Statistics
                </h2>
                <div className="grid md:grid-cols-5 gap-4">
                  <div 
                    onClick={() => setActiveTab('visitors')} 
                    className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-500 cursor-pointer hover:shadow-lg transition-shadow duration-200"
                  >
                    <h3 className="text-gray-600 text-sm mb-1">Total Visits</h3>
                    <p className="text-2xl font-bold text-blue-600">{visitorStats.total_visits}</p>
                  </div>
                  <div 
                    onClick={() => setActiveTab('visitors')} 
                    className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-green-500 cursor-pointer hover:shadow-lg transition-shadow duration-200"
                  >
                    <h3 className="text-gray-600 text-sm mb-1">Unique Visitors</h3>
                    <p className="text-2xl font-bold text-green-600">{visitorStats.unique_visitors}</p>
                  </div>
                  <div 
                    onClick={() => setActiveTab('visitors')} 
                    className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-purple-500 cursor-pointer hover:shadow-lg transition-shadow duration-200"
                  >
                    <h3 className="text-gray-600 text-sm mb-1">Today</h3>
                    <p className="text-2xl font-bold text-purple-600">{visitorStats.today_visits}</p>
                  </div>
                  <div 
                    onClick={() => setActiveTab('visitors')} 
                    className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-orange-500 cursor-pointer hover:shadow-lg transition-shadow duration-200"
                  >
                    <h3 className="text-gray-600 text-sm mb-1">This Week</h3>
                    <p className="text-2xl font-bold text-orange-600">{visitorStats.this_week_visits}</p>
                  </div>
                  <div 
                    onClick={() => setActiveTab('visitors')} 
                    className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-pink-500 cursor-pointer hover:shadow-lg transition-shadow duration-200"
                  >
                    <h3 className="text-gray-600 text-sm mb-1">This Month</h3>
                    <p className="text-2xl font-bold text-pink-600">{visitorStats.this_month_visits}</p>
                  </div>
                </div>
                
                {/* Page Views Breakdown */}
                <div className="mt-6 bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-gray-700 mb-3">Most Visited Pages</h3>
                  <div className="space-y-2">
                    {Object.entries(visitorStats.page_views).slice(0, 5).map(([page, count]) => (
                      <div key={page} className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm">{page}</span>
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">{count} views</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-bold mb-4">Recent Activity</h2>
              <div className="space-y-4">
                {quotes
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .slice(0, 5)
                  .map((quote) => (
                  <div key={quote.id} className="border-l-4 border-blue-600 pl-4 py-2">
                    <p className="font-medium">New quote request from {quote.name}</p>
                    <p className="text-sm text-gray-600">{new Date(quote.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* All Quotes Tab - Merged Manual + Calculator Quotes */}
        {activeTab === 'allQuotes' && (
          <div className="space-y-6">
            {/* Filters */}
            <FilterBar filterKey="quotes" />

            {/* Manual Quote Requests Section */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 bg-gray-100 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Manual Quote Requests ({filterData(quotes, 'quotes').length}/{quotesTotal})</h3>
                <p className="text-sm text-gray-600">Quotes submitted via contact form</p>
              </div>
              
              {/* Bulk Actions Bar */}
              {selectedItems.quotes.length > 0 && (
                <div className="px-6 py-3 bg-blue-50 border-b border-blue-200 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-blue-900">
                      {selectedItems.quotes.length} quote(s) selected
                    </span>
                    <button
                      onClick={() => clearSelection('quotes')}
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      Clear Selection
                    </button>
                  </div>
                  <button
                    onClick={handleBulkDeleteQuotes}
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm font-medium"
                  >
                    🗑️ Delete Selected
                  </button>
                </div>
              )}
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedItems.quotes.length === filterData(quotes, 'quotes').length && filterData(quotes, 'quotes').length > 0}
                          onChange={() => {
                            if (selectedItems.quotes.length === filterData(quotes, 'quotes').length) {
                              clearSelection('quotes');
                            } else {
                              selectAll('quotes', filterData(quotes, 'quotes'));
                            }
                          }}
                          className="rounded"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filterData(quotes, 'quotes').map((quote) => (
                      <tr key={quote.id}>
                        <td className="px-3 py-4">
                          <input
                            type="checkbox"
                            checked={selectedItems.quotes.includes(quote.id)}
                            onChange={() => toggleSelection('quotes', quote.id)}
                            className="rounded"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{quote.name}</td>
                        <td className="px-6 py-4">
                          <div className="text-sm">{quote.email}</div>
                          <div className="text-sm text-gray-500">{quote.phone}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{quote.service_type}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={quote.status}
                            onChange={(e) => updateQuoteStatus(quote.id, e.target.value)}
                            className="text-sm border rounded px-2 py-1"
                          >
                            <option value="pending">Pending</option>
                            <option value="contacted">Contacted</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {formatDateTime(quote.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => openDetailsModal(quote, 'quote')}
                            className="text-blue-600 hover:text-blue-800 mr-3"
                          >
                            View
                          </button>
                          {userRole === 'Super Administrator' && (
                            <button
                              onClick={() => deleteQuote(quote.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {quotesTotal > 0 && (
                <Pagination
                  currentPage={quotesPage}
                  totalItems={quotesTotal}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handleQuotesPageChange}
                />
              )}
            </div>

            {/* Instant Calculator Quotes Section */}
            <FilterBar filterKey="calculatedQuotes" showLocation={true} />

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 bg-blue-50 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Instant Calculator Quotes ({filterData(calculatedQuotes, 'calculatedQuotes').length}/{calculatedQuotesTotal})</h3>
                <p className="text-sm text-gray-600">Quotes generated via rate calculator</p>
              </div>
              
              {/* Bulk Actions Bar */}
              {selectedItems.calculatedQuotes.length > 0 && (
                <div className="px-6 py-3 bg-blue-50 border-b border-blue-200 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-blue-900">
                      {selectedItems.calculatedQuotes.length} quote(s) selected
                    </span>
                    <button
                      onClick={() => clearSelection('calculatedQuotes')}
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      Clear Selection
                    </button>
                  </div>
                  <button
                    onClick={handleBulkDeleteCalculatedQuotes}
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm font-medium"
                  >
                    🗑️ Delete Selected
                  </button>
                </div>
              )}
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedItems.calculatedQuotes.length === filterData(calculatedQuotes, 'calculatedQuotes').length && filterData(calculatedQuotes, 'calculatedQuotes').length > 0}
                          onChange={() => {
                            if (selectedItems.calculatedQuotes.length === filterData(calculatedQuotes, 'calculatedQuotes').length) {
                              clearSelection('calculatedQuotes');
                            } else {
                              selectAll('calculatedQuotes', filterData(calculatedQuotes, 'calculatedQuotes'));
                            }
                          }}
                          className="rounded"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Route</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Container</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Weight</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Distance</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filterData(calculatedQuotes, 'calculatedQuotes').map((quote) => (
                      <tr key={quote.id}>
                        <td className="px-3 py-4">
                          <input
                            type="checkbox"
                            checked={selectedItems.calculatedQuotes.includes(quote.id)}
                            onChange={() => toggleSelection('calculatedQuotes', quote.id)}
                            className="rounded"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium">{quote.customer_name}</div>
                          <div className="text-xs text-gray-500">{quote.customer_email}</div>
                          {quote.company_name && <div className="text-xs text-gray-500">{quote.company_name}</div>}
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm">{quote.pickup_location}</div>
                          <div className="text-xs text-gray-500">→ {quote.delivery_location}</div>
                        </td>
                        <td className="px-4 py-4 text-sm">{quote.container_type}</td>
                        <td className="px-4 py-4 text-sm">{quote.cargo_weight.toLocaleString()} lbs</td>
                        <td className="px-4 py-4 text-sm">{quote.distance.toFixed(0)} mi</td>
                        <td className="px-4 py-4 text-sm font-semibold text-green-600">${quote.total_rate.toFixed(2)}</td>
                        <td className="px-4 py-4 text-xs text-gray-500">
                          {formatDateTime(quote.created_at)}
                        </td>
                        <td className="px-4 py-4">
                          <button
                            onClick={() => openDetailsModal(quote, 'calculatedQuote')}
                            className="text-blue-600 hover:text-blue-800 text-sm mr-3"
                          >
                            View
                          </button>
                          {userRole === 'Super Administrator' && (
                            <button
                              onClick={() => deleteCalculatedQuote(quote.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {calculatedQuotesTotal > 0 && (
                <Pagination
                  currentPage={calculatedQuotesPage}
                  totalItems={calculatedQuotesTotal}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handleCalculatedQuotesPageChange}
                />
              )}
            </div>
          </div>
        )}

        {/* Old Quotes Tab - Removed */}
        {activeTab === 'quotes_old' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="quotes-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {quotes.map((quote) => (
                    <tr key={quote.id}>
                      <td className="px-6 py-4 whitespace-nowrap">{quote.name}</td>
                      <td className="px-6 py-4">
                        <div className="text-sm">{quote.email}</div>
                        <div className="text-sm text-gray-500">{quote.phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{quote.service_type}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={quote.status}
                          onChange={(e) => updateQuoteStatus(quote.id, e.target.value)}
                          className="text-sm border rounded px-2 py-1"
                        >
                          <option value="pending">Pending</option>
                          <option value="contacted">Contacted</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {new Date(quote.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => openDetailsModal(quote, 'quote')}
                          className="text-blue-600 hover:text-blue-800 mr-3"
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => deleteQuote(quote.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={quotesPage}
              totalItems={quotesTotal}
              itemsPerPage={itemsPerPage}
              onPageChange={handleQuotesPageChange}
            />
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div>
            <FilterBar filterKey="contacts" />
            <div className="bg-white rounded-lg shadow overflow-hidden">
              
              {/* Bulk Actions Bar */}
              {selectedItems.contacts.length > 0 && (
                <div className="px-6 py-3 bg-blue-50 border-b border-blue-200 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-blue-900">
                      {selectedItems.contacts.length} contact(s) selected
                    </span>
                    <button
                      onClick={() => clearSelection('contacts')}
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      Clear Selection
                    </button>
                  </div>
                  <button
                    onClick={handleBulkDeleteContacts}
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm font-medium"
                  >
                    🗑️ Delete Selected
                  </button>
                </div>
              )}
              
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="contacts-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedItems.contacts.length === filterData(contacts, 'contacts').length && filterData(contacts, 'contacts').length > 0}
                          onChange={() => {
                            if (selectedItems.contacts.length === filterData(contacts, 'contacts').length) {
                              clearSelection('contacts');
                            } else {
                              selectAll('contacts', filterData(contacts, 'contacts'));
                            }
                          }}
                          className="rounded"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filterData(contacts, 'contacts').map((contact) => (
                    <tr key={contact.id}>
                      <td className="px-3 py-4">
                        <input
                          type="checkbox"
                          checked={selectedItems.contacts.includes(contact.id)}
                          onChange={() => toggleSelection('contacts', contact.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{contact.name}</td>
                      <td className="px-6 py-4">
                        <div className="text-sm">{contact.email}</div>
                        <div className="text-sm text-gray-500">{contact.phone}</div>
                      </td>
                      <td className="px-6 py-4">{contact.subject || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={contact.status}
                          onChange={(e) => updateContactStatus(contact.id, e.target.value)}
                          className="text-sm border rounded px-2 py-1"
                        >
                          <option value="new">New</option>
                          <option value="read">Read</option>
                          <option value="responded">Responded</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {formatDateTime(contact.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => openDetailsModal(contact, 'contact')}
                          className="text-blue-600 hover:text-blue-800 mr-3"
                        >
                          View Details
                        </button>
                        {userRole === 'Super Administrator' && (
                          <button
                            onClick={() => deleteContact(contact.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              <Pagination
                currentPage={contactsPage}
                totalItems={contactsTotal}
                itemsPerPage={itemsPerPage}
                onPageChange={handleContactsPageChange}
              />
            </div>
          </div>
        )}

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <div>
            <FilterBar filterKey="applications" />
            <div className="bg-white rounded-lg shadow overflow-hidden">
              
              {/* Bulk Actions Bar */}
              {selectedItems.applications.length > 0 && (
                <div className="px-6 py-3 bg-blue-50 border-b border-blue-200 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-blue-900">
                      {selectedItems.applications.length} application(s) selected
                    </span>
                    <button
                      onClick={() => clearSelection('applications')}
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      Clear Selection
                    </button>
                  </div>
                  <button
                    onClick={handleBulkDeleteApplications}
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm font-medium"
                  >
                    🗑️ Delete Selected
                  </button>
                </div>
              )}
              
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="applications-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedItems.applications.length === filterData(applications, 'applications').length && filterData(applications, 'applications').length > 0}
                          onChange={() => {
                            if (selectedItems.applications.length === filterData(applications, 'applications').length) {
                              clearSelection('applications');
                            } else {
                              selectAll('applications', filterData(applications, 'applications'));
                            }
                          }}
                          className="rounded"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Experience</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filterData(applications, 'applications').map((app) => (
                    <tr key={app.id}>
                      <td className="px-3 py-4">
                        <input
                          type="checkbox"
                          checked={selectedItems.applications.includes(app.id)}
                          onChange={() => toggleSelection('applications', app.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{app.full_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap capitalize">
                        {app.application_type.replace('_', ' ')}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">{app.email}</div>
                        <div className="text-sm text-gray-500">{app.phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{app.years_experience || 'N/A'} years</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={app.status}
                          onChange={(e) => updateApplicationStatus(app.id, e.target.value)}
                          className="text-sm border rounded px-2 py-1"
                        >
                          <option value="pending">Pending</option>
                          <option value="reviewing">Reviewing</option>
                          <option value="interview">Interview</option>
                          <option value="hired">Hired</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {formatDateTime(app.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => openDetailsModal(app, 'application')}
                          className="text-blue-600 hover:text-blue-800 mr-3"
                        >
                          View Details
                        </button>
                        {userRole === 'Super Administrator' && (
                          <button
                            onClick={() => deleteApplication(app.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              <Pagination
                currentPage={applicationsPage}
                totalItems={applicationsTotal}
                itemsPerPage={itemsPerPage}
                onPageChange={handleApplicationsPageChange}
              />
            </div>
          </div>
        )}

        {/* Calculated Quotes Tab - Removed, merged into All Quotes */}
        {activeTab === 'calculatedQuotes_old' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="calculated-quotes-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Route</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Container</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Rate</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {calculatedQuotes.map((quote) => {
                    // Lead Scoring Logic
                    const isHighValue = quote.total_rate >= 1000; // $1000+ quotes
                    const isHeavyLoad = quote.cargo_weight >= 35000; // 35,000+ lbs
                    const isHotLead = isHighValue || isHeavyLoad;
                    
                    return (
                    <tr key={quote.id} className={isHotLead ? 'bg-yellow-50' : ''}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="text-sm font-medium">{quote.customer_name}</div>
                            <div className="text-sm text-gray-500">{quote.customer_email}</div>
                            <div className="text-sm text-gray-500">{quote.company_name}</div>
                          </div>
                          {isHotLead && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-300">
                              🔥 Hot Lead
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">{quote.pickup_location}</div>
                        <div className="text-sm text-gray-500">↓ {quote.distance} miles</div>
                        <div className="text-sm">{quote.delivery_location}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">{quote.container_type}</div>
                        <div className="text-sm text-gray-500">{quote.cargo_weight.toLocaleString()} lbs</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-semibold ${isHighValue ? 'text-orange-600' : 'text-green-600'}`}>
                          ${Math.round(quote.total_rate)}
                        </div>
                        {isHighValue && (
                          <div className="text-xs text-orange-600">High Value</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={quote.status}
                          onChange={(e) => updateCalculatedQuoteStatus(quote.id, e.target.value)}
                          className="text-sm border rounded px-2 py-1"
                        >
                          <option value="pending">Pending</option>
                          <option value="contacted">Contacted</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {new Date(quote.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => openDetailsModal(quote, 'calculatedQuote')}
                          className="text-blue-600 hover:text-blue-800 mr-3"
                        >
                          View Details
                        </button>
                        {userRole === 'Super Administrator' && (
                          <button
                            onClick={() => deleteCalculatedQuote(quote.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={calculatedQuotesPage}
              totalItems={calculatedQuotesTotal}
              itemsPerPage={itemsPerPage}
              onPageChange={handleCalculatedQuotesPageChange}
            />
          </div>
        )}

        {/* Internal Test Quotes Tab */}
        {activeTab === 'internalTestQuotes' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4 bg-blue-50 border-b border-blue-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <span className="mr-2">🧪</span>
                Internal Test Quotes
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Quotes submitted with admin@robandroberly.com (no email notifications sent)
              </p>
            </div>
            
            {/* Bulk Actions Bar */}
            {selectedItems.internalTestQuotes.length > 0 && (
              <div className="px-6 py-3 bg-blue-50 border-b border-blue-200 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-blue-900">
                    {selectedItems.internalTestQuotes.length} internal quote(s) selected
                  </span>
                  <button
                    onClick={() => clearSelection('internalTestQuotes')}
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Clear Selection
                  </button>
                </div>
                <button
                  onClick={handleBulkDeleteInternalQuotes}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm font-medium"
                >
                  🗑️ Delete Selected
                </button>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="internal-test-quotes-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedItems.internalTestQuotes.length === internalTestQuotes.length && internalTestQuotes.length > 0}
                        onChange={() => {
                          if (selectedItems.internalTestQuotes.length === internalTestQuotes.length) {
                            clearSelection('internalTestQuotes');
                          } else {
                            selectAll('internalTestQuotes', internalTestQuotes);
                          }
                        }}
                        className="rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Route</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Container</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Rate</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {internalTestQuotes.map((quote) => (
                    <tr key={quote.id} className="bg-blue-50">
                      <td className="px-3 py-4">
                        <input
                          type="checkbox"
                          checked={selectedItems.internalTestQuotes.includes(quote.id)}
                          onChange={() => toggleSelection('internalTestQuotes', quote.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium">{quote.customer_name}</div>
                          <div className="text-sm text-gray-500">{quote.customer_email}</div>
                          <div className="text-sm text-gray-500">{quote.company_name}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">{quote.pickup_location}</div>
                        <div className="text-sm text-gray-500">↓ {quote.distance} miles</div>
                        <div className="text-sm">{quote.delivery_location}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">{quote.container_type}</div>
                        <div className="text-sm text-gray-500">{quote.cargo_weight.toLocaleString()} lbs</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-blue-600">
                          ${Math.round(quote.total_rate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={quote.status}
                          onChange={(e) => updateInternalTestQuoteStatus(quote.id, e.target.value)}
                          className="text-sm border rounded px-2 py-1"
                        >
                          <option value="pending">Pending</option>
                          <option value="contacted">Contacted</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {new Date(quote.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => openDetailsModal(quote, 'internalTestQuote')}
                          className="text-blue-600 hover:text-blue-800 mr-3"
                        >
                          View Details
                        </button>
                        {userRole === 'Super Administrator' && (
                          <button
                            onClick={() => deleteInternalTestQuote(quote.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={internalTestQuotesPage}
              totalItems={internalTestQuotesTotal}
              itemsPerPage={itemsPerPage}
              onPageChange={handleInternalTestQuotesPageChange}
            />
          </div>
        )}

        {/* Bookings Tab - Compact */}
        {activeTab === 'bookings' && (
          <div>
            <FilterBar filterKey="bookings" showLocation={true} />
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-3 bg-green-50 border-b border-green-200 flex justify-between items-center">
                <h3 className="text-base font-semibold text-gray-800">📅 Active Bookings ({filterData(bookings, 'bookings').length}/{bookingsTotal})</h3>
                {selectedItems.bookings.length > 0 && (
                  <div className="flex gap-2 items-center">
                    <span className="text-xs text-gray-600">{selectedItems.bookings.length} selected</span>
                    <button
                      onClick={() => handleBulkDeleteBookings()}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs font-medium"
                    >
                      Delete ({selectedItems.bookings.length})
                    </button>
                    <button
                      onClick={() => clearSelection('bookings')}
                      className="px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-xs"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="bookings-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-2 w-8">
                        <input
                          type="checkbox"
                          onChange={(e) => e.target.checked ? selectAll('bookings', filterData(bookings, 'bookings')) : clearSelection('bookings')}
                          checked={selectedItems.bookings.length > 0 && selectedItems.bookings.length === filterData(bookings, 'bookings').length}
                          className="rounded border-gray-300"
                        />
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Ref & Date</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Container/BOL</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Rate</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filterData(bookings, 'bookings').map((booking) => (
                    <tr key={booking.id} className="hover:bg-gray-50">
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={selectedItems.bookings.includes(booking.id)}
                          onChange={() => toggleSelection('bookings', booking.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-blue-600">{booking.booking_reference}</div>
                        <div className="text-gray-500">{formatDateTime(booking.created_at)}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium">{booking.customer_name}</div>
                        <div className="text-gray-500">{booking.customer_email}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-blue-700">
                          {booking.container_trailer_numbers?.[0]?.number || 'N/A'}
                          {booking.container_trailer_numbers?.length > 1 && ` +${booking.container_trailer_numbers.length - 1}`}
                        </div>
                        <div className="text-gray-500">
                          {booking.bill_of_lading_numbers?.[0]?.number || 'N/A'}
                          {booking.bill_of_lading_numbers?.length > 1 && ` +${booking.bill_of_lading_numbers.length - 1}`}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-semibold text-green-600">
                        ${Math.round(booking.total_rate)}
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={booking.status}
                          onChange={(e) => updateBookingStatus(booking.id, e.target.value)}
                          className={`text-xs border rounded px-1 py-1 ${
                            booking.status === 'pending' ? 'bg-yellow-50 border-yellow-300' :
                            booking.status === 'confirmed' ? 'bg-blue-50 border-blue-300' :
                            booking.status === 'in_transit' ? 'bg-purple-50 border-purple-300' :
                            booking.status === 'delivered' ? 'bg-green-50 border-green-300' :
                            booking.status === 'completed' ? 'bg-gray-50 border-gray-300' : ''
                          }`}
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="in_transit">In Transit</option>
                          <option value="delivered">Delivered</option>
                          <option value="completed">Completed</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => openDetailsModal(booking, 'booking')}
                          className="text-blue-600 hover:text-blue-800 mr-2"
                        >
                          View
                        </button>
                        {userRole === 'Super Administrator' && (
                          <button
                            onClick={() => deleteBooking(booking.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Del
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              <Pagination
                currentPage={bookingsPage}
                totalItems={bookingsTotal}
                itemsPerPage={itemsPerPage}
                onPageChange={handleBookingsPageChange}
              />
            </div>
          </div>
        )}

        {/* Completed Bookings Tab - Compact */}
        {activeTab === 'completedBookings' && (
          <div>
            <FilterBar filterKey="completedBookings" showLocation={true} />
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-3 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-base font-semibold text-gray-800">✅ Completed Bookings ({filterData(completedBookings, 'completedBookings').length}/{completedBookingsTotal})</h3>
                {selectedItems.completedBookings.length > 0 && (
                  <div className="flex gap-2 items-center">
                    <span className="text-xs text-gray-600">{selectedItems.completedBookings.length} selected</span>
                    <button
                      onClick={() => handleBulkDeleteCompletedBookings()}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs font-medium"
                    >
                      Delete ({selectedItems.completedBookings.length})
                    </button>
                    <button
                      onClick={() => clearSelection('completedBookings')}
                      className="px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-xs"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="completed-bookings-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 w-8">
                      <input
                        type="checkbox"
                        onChange={(e) => e.target.checked ? selectAll('completedBookings', filterData(completedBookings, 'completedBookings')) : clearSelection('completedBookings')}
                        checked={selectedItems.completedBookings.length > 0 && selectedItems.completedBookings.length === filterData(completedBookings, 'completedBookings').length}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Booking Reference</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Customer</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Container/BOL</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Rate</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Completed Date</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filterData(completedBookings, 'completedBookings').map((booking) => (
                    <tr key={booking.id} className="hover:bg-gray-50 bg-green-50">
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={selectedItems.completedBookings.includes(booking.id)}
                          onChange={() => toggleSelection('completedBookings', booking.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-blue-600">{booking.booking_reference}</div>
                        <div className="text-gray-500">Created: {formatDateTime(booking.created_at)}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium">{booking.customer_name}</div>
                        <div className="text-gray-500">{booking.customer_email}</div>
                        <div className="text-gray-500">{booking.company_name}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-blue-700 font-medium">
                          {booking.container_trailer_numbers?.map(item => item.number).join(', ') || 'N/A'}
                        </div>
                        <div className="text-gray-600">
                          {booking.bill_of_lading_numbers?.map(item => item.number).join(', ') || 'N/A'}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-semibold text-green-600">
                        ${Math.round(booking.total_rate)}
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-gray-900">{formatDateTime(booking.updated_at)}</div>
                        <div className="text-gray-500">{new Date(booking.updated_at).toLocaleTimeString()}</div>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => openDetailsModal(booking, 'booking')}
                          className="text-blue-600 hover:text-blue-800 mr-2"
                        >
                          View
                        </button>
                        {userRole === 'Super Administrator' && (
                          <button
                            onClick={() => deleteBooking(booking.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Del
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              <Pagination
                currentPage={completedBookingsPage}
                totalItems={completedBookingsTotal}
                itemsPerPage={itemsPerPage}
                onPageChange={handleCompletedBookingsPageChange}
              />
            </div>
          </div>
        )}

        {/* Visitors Tab */}
        {activeTab === 'visitors' && (
          <div>
            <FilterBar filterKey="visitors" showIP={true} />
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  Detailed Visitor Log ({filterData(visitors, 'visitors').length} records)
                </h2>
                <p className="text-sm text-gray-600 mt-1">View individual visitor details including timestamps, pages visited, and IP addresses</p>
              </div>
              
              {/* Bulk Actions Bar */}
              {selectedItems.visitors.length > 0 && (
                <div className="px-6 py-3 bg-blue-50 border-b border-blue-200 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-blue-900">
                      {selectedItems.visitors.length} visitor(s) selected
                    </span>
                    <button
                      onClick={() => clearSelection('visitors')}
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      Clear Selection
                    </button>
                  </div>
                  <button
                    onClick={handleBulkDeleteVisitors}
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm font-medium"
                  >
                    🗑️ Delete Selected
                  </button>
                </div>
              )}
              
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="visitors-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedItems.visitors.length === filterData(visitors, 'visitors').length && filterData(visitors, 'visitors').length > 0}
                        onChange={() => {
                          if (selectedItems.visitors.length === filterData(visitors, 'visitors').length) {
                            clearSelection('visitors');
                          } else {
                            selectAll('visitors', filterData(visitors, 'visitors'));
                          }
                        }}
                        className="rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page Visited</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Agent</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filterData(visitors, 'visitors').map((visitor) => (
                    <tr key={visitor.id} className="hover:bg-gray-50">
                      <td className="px-3 py-4">
                        <input
                          type="checkbox"
                          checked={selectedItems.visitors.includes(visitor.id)}
                          onChange={() => toggleSelection('visitors', visitor.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatDateTime(visitor.timestamp)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {new Date(visitor.timestamp).toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                          {visitor.page}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {visitor.location_city || visitor.location_region || visitor.location_country ? (
                          <div>
                            <div className="font-medium">
                              {visitor.location_city && visitor.location_region 
                                ? `${visitor.location_city}, ${visitor.location_region}`
                                : visitor.location_city || visitor.location_region || 'Unknown'}
                            </div>
                            <div className="text-gray-500 text-xs">
                              {visitor.location_country || 'Unknown Country'}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">Unknown Location</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {visitor.ip_address || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-md truncate">
                        {visitor.user_agent || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {userRole === 'Super Administrator' && (
                          <button
                            onClick={() => deleteVisitor(visitor.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filterData(visitors, 'visitors').length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <p>No visitor data available yet</p>
              </div>
            )}
              <Pagination
                currentPage={visitorsPage}
                totalItems={visitorsTotal}
                itemsPerPage={itemsPerPage}
                onPageChange={handleVisitorsPageChange}
              />
            </div>
          </div>
        )}

        {/* Portal Access Requests Tab */}
        {activeTab === 'portalAccessRequests' && (
          <div className="space-y-6">
            {/* Filter for Portal Users */}
            <FilterBar filterKey="portalUsers" />
            
            {/* Registered Portal Users Section */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 bg-green-50 border-b flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Registered Portal Users ({filterData(portalUsers, 'portalUsers').length}/{portalUsers.length})</h2>
                  <p className="text-sm text-gray-600 mt-1">All customers with portal accounts</p>
                </div>
                {selectedItems.portalUsers.length > 0 && (
                  <div className="flex gap-2 items-center">
                    <span className="text-sm text-gray-600">{selectedItems.portalUsers.length} selected</span>
                    <button
                      onClick={() => handleBulkDeletePortalUsers()}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm font-medium"
                    >
                      Delete Selected ({selectedItems.portalUsers.length})
                    </button>
                    <button
                      onClick={() => clearSelection('portalUsers')}
                      className="px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
              
              {portalUsers.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  <p>No registered portal users yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12">
                          <input
                            type="checkbox"
                            onChange={(e) => {
                              if (e.target.checked) {
                                selectAll('portalUsers', filterData(portalUsers, 'portalUsers'));
                              } else {
                                clearSelection('portalUsers');
                              }
                            }}
                            checked={selectedItems.portalUsers.length > 0 && selectedItems.portalUsers.length === filterData(portalUsers, 'portalUsers').length}
                            className="rounded border-gray-300"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filterData(portalUsers, 'portalUsers').map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedItems.portalUsers.includes(user.id)}
                              onChange={() => toggleSelection('portalUsers', user.id)}
                              className="rounded border-gray-300"
                            />
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap font-medium text-gray-900">{user.name || 'N/A'}</td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-blue-600">{user.email}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-gray-600 text-sm">{user.company_name || 'N/A'}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-gray-600 text-sm">{user.phone || 'N/A'}</td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              user.status === 'active' ? 'bg-green-100 text-green-800' :
                              user.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {(user.status || 'active').charAt(0).toUpperCase() + (user.status || 'active').slice(1)}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-xs text-gray-500">
                            {user.created_at ? formatDateTime(user.created_at) : 'N/A'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-xs text-gray-500">
                            {user.last_login ? formatDateTime(user.last_login) : 'Never'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <select
                              value=""
                              onChange={(e) => {
                                const action = e.target.value;
                                if (action === 'activate') handleActivateUser(user.id);
                                else if (action === 'suspend') handleSuspendUser(user.id);
                                else if (action === 'revoke') handleRevokeUser(user.id);
                                else if (action === 'reset_pw') handleResetUserPassword(user.id, user.email);
                                else if (action === 'delete') handleDeletePortalUser(user.id);
                                e.target.value = ''; // Reset dropdown
                              }}
                              className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="">Actions ▼</option>
                              {user.status !== 'active' && <option value="activate">✓ Activate</option>}
                              {user.status === 'active' && <option value="suspend">⏸ Suspend</option>}
                              {user.status !== 'revoked' && <option value="revoke">🚫 Revoke Access</option>}
                              <option value="reset_pw">🔑 Reset Password</option>
                              <option value="delete">🗑️ Delete</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Filter for Access Requests */}
            <FilterBar filterKey="accessRequests" />
            
            {/* Access Requests Section */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 bg-blue-50 border-b flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Pending Access Requests ({filterData(portalAccessRequests, 'accessRequests').length}/{portalAccessRequests.length})</h2>
                  <p className="text-sm text-gray-600 mt-1">Review and approve customer portal access requests</p>
                </div>
                {selectedItems.accessRequests.length > 0 && (
                  <div className="flex gap-2 items-center">
                    <span className="text-sm text-gray-600">{selectedItems.accessRequests.length} selected</span>
                    <button
                      onClick={() => handleBulkDeleteAccessRequests()}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm font-medium"
                    >
                      Delete Selected ({selectedItems.accessRequests.length})
                    </button>
                    <button
                      onClick={() => clearSelection('accessRequests')}
                      className="px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
            
            {portalAccessRequests.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p>No portal access requests at this time</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12">
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              selectAll('accessRequests', filterData(portalAccessRequests, 'accessRequests'));
                            } else {
                              clearSelection('accessRequests');
                            }
                          }}
                          checked={selectedItems.accessRequests.length > 0 && selectedItems.accessRequests.length === filterData(portalAccessRequests, 'accessRequests').length}
                          className="rounded border-gray-300"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filterData(portalAccessRequests, 'accessRequests').map((request) => (
                      <tr key={request.id}>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedItems.accessRequests.includes(request.id)}
                            onChange={() => toggleSelection('accessRequests', request.id)}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap font-medium">{request.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{request.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{request.company_name || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{request.phone}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            request.status === 'approved' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {formatDateTime(request.requested_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value=""
                            onChange={(e) => {
                              const action = e.target.value;
                              if (!action) return; // Ignore if "Actions" is selected
                              
                              // Execute action based on selection
                              if (action === 'approve') {
                                handleApproveAccessRequest(request.id);
                              } else if (action === 'reject') {
                                handleRejectAccessRequest(request.id);
                              } else if (action === 'pending') {
                                handleRevertAccessRequest(request.id);
                              } else if (action === 'delete') {
                                handleDeleteAccessRequest(request.id);
                              }
                              
                              // Reset dropdown to default
                              setTimeout(() => {
                                e.target.value = '';
                              }, 100);
                            }}
                            className="text-sm border border-gray-300 rounded px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer hover:border-gray-400 bg-white"
                          >
                            <option value="">Actions ▼</option>
                            {request.status !== 'approved' && <option value="approve">✓ Approve</option>}
                            {request.status === 'pending' && <option value="reject">✗ Reject</option>}
                            {request.status === 'rejected' && <option value="pending">🔄 Revert to Pending</option>}
                            <option value="delete">🗑️ Delete</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            </div>
          </div>
        )}

        {/* Details Modal */}
        {selectedItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold">
                    {modalType === 'contact' && 'Contact Details'}
                    {modalType === 'application' && 'Application Details'}
                    {modalType === 'quote' && 'Quote Details'}
                    {modalType === 'calculatedQuote' && 'Calculated Rate Quote Details'}
                    {modalType === 'internalTestQuote' && 'Internal Test Quote Details'}
                    {modalType === 'booking' && 'Booking Details'}
                  </h2>
                  <button onClick={closeModal} className="text-gray-500 hover:text-gray-700 text-2xl">
                    ×
                  </button>
                </div>

                {/* Contact Details */}
                {modalType === 'contact' && (
                  <div className="space-y-4">
                    <div>
                      <label className="font-semibold text-gray-700">Name:</label>
                      <p className="text-gray-900">{selectedItem.name}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Email:</label>
                      <p className="text-gray-900">{selectedItem.email}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Phone:</label>
                      <p className="text-gray-900">{selectedItem.phone}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Subject:</label>
                      <p className="text-gray-900">{selectedItem.subject}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Message:</label>
                      <p className="text-gray-900 whitespace-pre-wrap bg-gray-50 p-4 rounded">{selectedItem.message}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Status:</label>
                      <p className="text-gray-900">{selectedItem.status}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Submitted:</label>
                      <p className="text-gray-900">{new Date(selectedItem.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                )}

                {/* Application Details */}
                {modalType === 'application' && (
                  <div className="space-y-4">
                    <div>
                      <label className="font-semibold text-gray-700">Application Type:</label>
                      <p className="text-gray-900">{selectedItem.application_type === 'company_driver' ? 'Company Driver' : 'Owner Operator'}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Full Name:</label>
                      <p className="text-gray-900">{selectedItem.full_name}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Email:</label>
                      <p className="text-gray-900">{selectedItem.email}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Phone:</label>
                      <p className="text-gray-900">{selectedItem.phone}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Address:</label>
                      <p className="text-gray-900">{selectedItem.address}, {selectedItem.city}, {selectedItem.state} {selectedItem.zip_code}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">CDL Number:</label>
                      <p className="text-gray-900">{selectedItem.cdl_number}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">CDL State:</label>
                      <p className="text-gray-900">{selectedItem.cdl_state}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Years of Experience:</label>
                      <p className="text-gray-900">{selectedItem.years_experience} years</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Previous Employer:</label>
                      <p className="text-gray-900">{selectedItem.previous_employer || 'N/A'}</p>
                    </div>
                    {selectedItem.truck_info && (
                      <div>
                        <label className="font-semibold text-gray-700">Truck Information:</label>
                        <p className="text-gray-900 whitespace-pre-wrap bg-gray-50 p-4 rounded">{selectedItem.truck_info}</p>
                      </div>
                    )}
                    {selectedItem.additional_info && (
                      <div>
                        <label className="font-semibold text-gray-700">Additional Information:</label>
                        <p className="text-gray-900 whitespace-pre-wrap bg-gray-50 p-4 rounded">{selectedItem.additional_info}</p>
                      </div>
                    )}
                    <div>
                      <label className="font-semibold text-gray-700">Status:</label>
                      <p className="text-gray-900">{selectedItem.status}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Submitted:</label>
                      <p className="text-gray-900">{new Date(selectedItem.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                )}

                {/* Quote Details */}
                {modalType === 'quote' && (
                  <div className="space-y-4">
                    <div>
                      <label className="font-semibold text-gray-700">Name:</label>
                      <p className="text-gray-900">{selectedItem.name}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Email:</label>
                      <p className="text-gray-900">{selectedItem.email}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Phone:</label>
                      <p className="text-gray-900">{selectedItem.phone}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Service Type:</label>
                      <p className="text-gray-900">{selectedItem.service_type}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Details:</label>
                      <p className="text-gray-900 whitespace-pre-wrap bg-gray-50 p-4 rounded">{selectedItem.details}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Status:</label>
                      <p className="text-gray-900">{selectedItem.status}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Submitted:</label>
                      <p className="text-gray-900">{new Date(selectedItem.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                )}

                {/* Calculated Quote Details */}
                {(modalType === 'calculatedQuote' || modalType === 'internalTestQuote') && (
                  <div className="space-y-4">
                    {modalType === 'internalTestQuote' && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <p className="text-sm text-blue-800 flex items-center">
                          <span className="mr-2">ℹ️</span>
                          This is an internal test quote. No email notification was sent.
                        </p>
                      </div>
                    )}
                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Customer Information</h3>
                    <div>
                      <label className="font-semibold text-gray-700">Name:</label>
                      <p className="text-gray-900">{selectedItem.customer_name}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Email:</label>
                      <p className="text-gray-900">{selectedItem.customer_email}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Company:</label>
                      <p className="text-gray-900">{selectedItem.company_name}</p>
                    </div>
                    
                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mt-6">Shipment Details</h3>
                    <div>
                      <label className="font-semibold text-gray-700">Pickup Location:</label>
                      <p className="text-gray-900">{selectedItem.pickup_location}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Delivery Location:</label>
                      <p className="text-gray-900">{selectedItem.delivery_location}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Distance:</label>
                      <p className="text-gray-900">{selectedItem.distance} miles ({selectedItem.estimated_duration.toFixed(1)} hours)</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Container Type:</label>
                      <p className="text-gray-900">{selectedItem.container_type}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Cargo Weight:</label>
                      <p className="text-gray-900">{selectedItem.cargo_weight.toLocaleString()} lbs</p>
                    </div>
                    
                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mt-6">Rate Breakdown</h3>
                    <div className="bg-gray-50 p-4 rounded">
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-700">Base Rate:</span>
                        <span className="font-semibold">${selectedItem.base_rate.toFixed(2)}</span>
                      </div>
                      {selectedItem.accessorials && selectedItem.accessorials.map((acc, index) => (
                        <div key={index} className="flex justify-between mb-2">
                          <span className="text-gray-700">{acc.name}:</span>
                          <span className="font-semibold">${acc.amount.toFixed(2)}</span>
                        </div>
                      ))}
                      <div className="border-t pt-2 mt-2 flex justify-between">
                        <span className="font-bold text-lg">Total Rate:</span>
                        <span className="font-bold text-lg text-green-600">${Math.round(selectedItem.total_rate)}</span>
                      </div>
                    </div>
                    
                    <div>
                      <label className="font-semibold text-gray-700">Status:</label>
                      <p className="text-gray-900 capitalize">{selectedItem.status}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Calculated On:</label>
                      <p className="text-gray-900">{new Date(selectedItem.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                )}

                {/* Booking Details */}
                {modalType === 'booking' && (
                  <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-green-800 flex items-center">
                        <span className="mr-2">📦</span>
                        <strong>Booking Reference:</strong> <span className="ml-2 text-blue-600 font-bold">{selectedItem.booking_reference}</span>
                      </p>
                    </div>

                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Customer Information</h3>
                    <div>
                      <label className="font-semibold text-gray-700">Name:</label>
                      <p className="text-gray-900">{selectedItem.customer_name}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Email (Quote):</label>
                      <p className="text-gray-900">{selectedItem.customer_email}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Notification Email:</label>
                      <p className="text-gray-900 bg-yellow-50 p-2 rounded border border-yellow-200">
                        {selectedItem.notification_email}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">All booking updates are sent to this email</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Phone:</label>
                      <p className="text-gray-900">{selectedItem.phone}</p>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Company:</label>
                      <p className="text-gray-900">{selectedItem.company_name}</p>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mt-6">Vessel/Terminal Information</h3>
                    {selectedItem.vessel_eta && (
                      <div>
                        <label className="font-semibold text-gray-700">Vessel ETA:</label>
                        <p className="text-gray-900">{selectedItem.vessel_eta}</p>
                      </div>
                    )}
                    {selectedItem.terminal_receiving_date && (
                      <div>
                        <label className="font-semibold text-gray-700">Terminal Receiving Date:</label>
                        <p className="text-gray-900">{selectedItem.terminal_receiving_date}</p>
                      </div>
                    )}
                    {selectedItem.terminal_cut_off_date && (
                      <div>
                        <label className="font-semibold text-gray-700">Terminal Cut Off Date:</label>
                        <p className="text-gray-900">{selectedItem.terminal_cut_off_date}</p>
                      </div>
                    )}
                    <div>
                      <label className="font-semibold text-gray-700">Container/Trailer Numbers:</label>
                      <div className="bg-blue-50 p-3 rounded border border-blue-200 space-y-2">
                        {selectedItem.container_trailer_numbers?.map((item, index) => (
                          <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                            <span className="font-medium">{item.number}</span>
                            <select
                              value={item.status}
                              onChange={async (e) => {
                                try {
                                  await axios.patch(
                                    `${API}/admin/bookings/${selectedItem.id}/container-bol-status`,
                                    { container_index: index, status: e.target.value, update_all: false },
                                    getAuthHeaders()
                                  );
                                  fetchBookings(bookingsPage);
                                  closeModal();
                                } catch (err) {
                                  console.error('Failed to update container status');
                                }
                              }}
                              className="text-sm border rounded px-2 py-1"
                            >
                              <option value="pending">Pending</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="in_transit">In Transit</option>
                              <option value="delivered">Delivered</option>
                              <option value="completed">Completed</option>
                            </select>
                          </div>
                        )) || <span className="text-gray-500">N/A</span>}
                        {selectedItem.container_trailer_numbers?.length > 1 && (
                          <button
                            onClick={async () => {
                              const newStatus = prompt('Update all containers to status (pending, confirmed, in_transit, delivered, completed):');
                              if (newStatus && ['pending', 'confirmed', 'in_transit', 'delivered', 'completed'].includes(newStatus)) {
                                try {
                                  await axios.patch(
                                    `${API}/admin/bookings/${selectedItem.id}/container-bol-status`,
                                    { status: newStatus, update_all: true },
                                    getAuthHeaders()
                                  );
                                  fetchBookings(bookingsPage);
                                  closeModal();
                                } catch (err) {
                                  console.error('Failed to update all containers');
                                }
                              }
                            }}
                            className="w-full text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                          >
                            Update All Containers
                          </button>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700">Bill of Lading/Booking Numbers:</label>
                      <div className="bg-blue-50 p-3 rounded border border-blue-200 space-y-2">
                        {selectedItem.bill_of_lading_numbers?.map((item, index) => (
                          <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                            <span className="font-medium">{item.number}</span>
                            <select
                              value={item.status}
                              onChange={async (e) => {
                                try {
                                  await axios.patch(
                                    `${API}/admin/bookings/${selectedItem.id}/container-bol-status`,
                                    { bol_index: index, status: e.target.value, update_all: false },
                                    getAuthHeaders()
                                  );
                                  fetchBookings(bookingsPage);
                                  closeModal();
                                } catch (err) {
                                  console.error('Failed to update BOL status');
                                }
                              }}
                              className="text-sm border rounded px-2 py-1"
                            >
                              <option value="pending">Pending</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="in_transit">In Transit</option>
                              <option value="delivered">Delivered</option>
                              <option value="completed">Completed</option>
                            </select>
                          </div>
                        )) || <span className="text-gray-500">N/A</span>}
                        {selectedItem.bill_of_lading_numbers?.length > 1 && (
                          <button
                            onClick={async () => {
                              const newStatus = prompt('Update all BOLs to status (pending, confirmed, in_transit, delivered, completed):');
                              if (newStatus && ['pending', 'confirmed', 'in_transit', 'delivered', 'completed'].includes(newStatus)) {
                                try {
                                  await axios.patch(
                                    `${API}/admin/bookings/${selectedItem.id}/container-bol-status`,
                                    { status: newStatus, update_all: true },
                                    getAuthHeaders()
                                  );
                                  fetchBookings(bookingsPage);
                                  closeModal();
                                } catch (err) {
                                  console.error('Failed to update all BOLs');
                                }
                              }
                            }}
                            className="w-full text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                          >
                            Update All BOL Numbers
                          </button>
                        )}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mt-6">Pickup & Delivery Schedule</h3>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        {selectedItem.pickup_date && (
                          <div>
                            <label className="font-semibold text-gray-700">Pickup Date:</label>
                            <p className="text-gray-900">{selectedItem.pickup_date}</p>
                          </div>
                        )}
                        {selectedItem.pickup_time && (
                          <div>
                            <label className="font-semibold text-gray-700">Pickup Time:</label>
                            <p className="text-gray-900">{selectedItem.pickup_time}</p>
                          </div>
                        )}
                        {selectedItem.delivery_date && (
                          <div>
                            <label className="font-semibold text-gray-700">Delivery Date:</label>
                            <p className="text-gray-900">{selectedItem.delivery_date}</p>
                          </div>
                        )}
                        {selectedItem.delivery_time && (
                          <div>
                            <label className="font-semibold text-gray-700">Delivery Time:</label>
                            <p className="text-gray-900">{selectedItem.delivery_time}</p>
                          </div>
                        )}
                        {selectedItem.preferred_contact_method && (
                          <div>
                            <label className="font-semibold text-gray-700">Preferred Contact Method:</label>
                            <p className="text-gray-900 capitalize">{selectedItem.preferred_contact_method}</p>
                          </div>
                        )}
                        {selectedItem.additional_contact_person && (
                          <div>
                            <label className="font-semibold text-gray-700">Additional Contact:</label>
                            <p className="text-gray-900">{selectedItem.additional_contact_person}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mt-6">Shipment Details</h3>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="font-semibold text-gray-700">Pickup Location:</label>
                          <p className="text-gray-900">{selectedItem.pickup_location}</p>
                        </div>
                        <div>
                          <label className="font-semibold text-gray-700">Delivery Location:</label>
                          <p className="text-gray-900">{selectedItem.delivery_location}</p>
                        </div>
                        <div>
                          <label className="font-semibold text-gray-700">Distance:</label>
                          <p className="text-gray-900">{selectedItem.distance} miles</p>
                        </div>
                        <div>
                          <label className="font-semibold text-gray-700">Container Type:</label>
                          <p className="text-gray-900">{selectedItem.container_type}</p>
                        </div>
                        <div>
                          <label className="font-semibold text-gray-700">Cargo Weight:</label>
                          <p className="text-gray-900">{selectedItem.cargo_weight.toLocaleString()} lbs</p>
                        </div>
                      </div>
                    </div>

                    {selectedItem.special_instructions && (
                      <div>
                        <label className="font-semibold text-gray-700">Special Instructions:</label>
                        <p className="text-gray-900 bg-yellow-50 p-3 rounded border border-yellow-200">{selectedItem.special_instructions}</p>
                      </div>
                    )}

                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mt-6">Rate Breakdown</h3>
                      <div className="bg-gray-50 p-4 rounded">
                        <div className="flex justify-between mb-2">
                          <span className="text-gray-700">Base Rate:</span>
                          <span className="font-semibold">${selectedItem.base_rate.toFixed(2)}</span>
                        </div>
                        {selectedItem.accessorials && selectedItem.accessorials.map((acc, index) => (
                          <div key={index} className="flex justify-between mb-2">
                            <span className="text-gray-700">{acc.name}:</span>
                            <span className="font-semibold">${acc.amount.toFixed(2)}</span>
                          </div>
                        ))}
                        <div className="border-t pt-2 mt-2 flex justify-between">
                          <span className="font-bold text-lg">Total Rate:</span>
                          <span className="font-bold text-lg text-green-600">${Math.round(selectedItem.total_rate)}</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="font-semibold text-gray-700">Status:</label>
                        <p className="text-gray-900 capitalize">{selectedItem.status}</p>
                      </div>
                      <div>
                        <label className="font-semibold text-gray-700">Booked On:</label>
                        <p className="text-gray-900">{new Date(selectedItem.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                    </div>
                  </div>
                )}

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={closeModal}
                    className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Security Tab - Rate Limiting & Blocked IPs */}
        {activeTab === 'security' && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">🔒 Security & Rate Limiting</h2>

            <SecurityTab getAuthHeaders={getAuthHeaders} />
          </div>
        )}


        {/* Backups Tab */}
        {activeTab === 'backups' && (
          <div>
            <BackupManager getAuthHeaders={getAuthHeaders} />
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div>
            <SettingsTab getAuthHeaders={getAuthHeaders} />
          </div>
        )}

        {/* Activity Log Tab */}
        {activeTab === 'activityLog' && (
          <div>
            <ActivityLogTab getAuthHeaders={getAuthHeaders} />
          </div>
        )}

        {/* Recycle Bin Tab */}
        {activeTab === 'recycleBin' && (
          <div>
            <RecycleBinTab getAuthHeaders={getAuthHeaders} />
          </div>
        )}

        {/* Testimonials Tab */}
        {activeTab === 'testimonials' && (
          <div>
            <TestimonialsTab getAuthHeaders={getAuthHeaders} />
          </div>
        )}


      </div>
    </div>
  );
};

export default { AdminLogin, AdminDashboard };
